import {
  AiFeedbackRating,
  Review,
  useAiFeedbackRating,
  useApproveMentorTraining,
  useSubmitFeedback,
} from "@/lib/api/training-simulator/training-simulator-review";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { Button, IconButton } from "@somethings-ui/web";
import { useNavigate } from "@tanstack/react-router";
import { Sparkle, ThumbsDown, ThumbsUp } from "lucide-react";
import { useState } from "react";

export function TrainingReviewFeedback({
  review,
  progressId,
  isApproved,
}: {
  progressId: string;
  review: Review | null;
  isApproved: boolean;
}) {
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState("");
  const { track } = useTrackEvent();
  const aiFeedbackMutation = useAiFeedbackRating(progressId);
  const submitFeedbackMutation = useSubmitFeedback(progressId);
  const approveProgressMutation = useApproveMentorTraining(progressId);

  const handleAiFeedback = (rating: AiFeedbackRating) => {
    track(PostHogEvents.SIMULATOR_LM_AI_FEEDBACK_RATE, {
      progress_id: progressId,
      rating,
    });
    aiFeedbackMutation.mutate({ rating });
  };

  const handleSubmitFeedback = async (feedback: string) => {
    const trimmed = feedback.trim();
    track(PostHogEvents.SIMULATOR_LM_FEEDBACK_SUBMIT, {
      progress_id: progressId,
      char_count: trimmed.length,
    });

    try {
      await submitFeedbackMutation.mutateAsync({ feedback: trimmed });
      setFeedback("");
    } catch {
      return;
    }
  };

  const handleApprove = async () => {
    try {
      await approveProgressMutation.mutateAsync();
      navigate({
        to: "/training/review",
      });
    } catch {
      return;
    }
  };

  return (
    <div className="grid grid-cols-3 gap-4">
      <div className="col-span-1 rounded-sm border-green-100 bg-white p-4">
        <div className="mb-4 flex flex-row items-center gap-2">
          <Sparkle className="size-3 opacity-50" />
          <span className="text-body-3 text-tertiary">AI Feedback</span>
        </div>
        {review?.ai_feedback ? (
          <>
            <p className="mb-4 h-[120px] overflow-y-auto text-body-3 text-primary">
              {review?.ai_feedback}
            </p>

            <div className="flex flex-row items-center justify-between">
              <span className="flex-1 text-body-3 text-tertiary">
                Rate this feedback
              </span>
              <div className="flex flex-row gap-2">
                <IconButton
                  size="sm"
                  variant="ghost-black"
                  icon={
                    <ThumbsUp
                      fill={
                        review.ai_feedback_rating === AiFeedbackRating.THUMBS_UP
                          ? "black"
                          : "none"
                      }
                    />
                  }
                  disabled={aiFeedbackMutation.isPending}
                  onClick={() => {
                    handleAiFeedback(AiFeedbackRating.THUMBS_UP);
                  }}
                />
                <IconButton
                  size="sm"
                  variant="ghost-black"
                  icon={
                    <ThumbsDown
                      fill={
                        review.ai_feedback_rating ===
                        AiFeedbackRating.THUMBS_DOWN
                          ? "black"
                          : "none"
                      }
                    />
                  }
                  disabled={aiFeedbackMutation.isPending}
                  onClick={() => {
                    handleAiFeedback(AiFeedbackRating.THUMBS_DOWN);
                  }}
                />
              </div>
            </div>
          </>
        ) : (
          <p className="mb-4 h-[120px] overflow-y-auto text-body-3 text-primary">
            Sorry, AI feedback could not be generated at this time. Please try
            again later or contact support with further questions
          </p>
        )}
      </div>

      <div className="col-span-2 flex min-h-[220px] flex-col rounded-sm border-green-100 bg-white p-4">
        <textarea
          className="min-h-32 flex-1 resize-none bg-transparent text-body-2 text-text-primary outline-none placeholder:text-secondary focus:ring-0"
          placeholder="Leave feedback for the entire performance, not just level-based performance."
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
        />
        <div className="mt-4 flex justify-end gap-4">
          <Button
            variant="primary"
            fullWidth={false}
            disabled={!feedback.trim() || submitFeedbackMutation.isPending}
            loading={submitFeedbackMutation.isPending}
            onClick={() => handleSubmitFeedback(feedback)}
          >
            Submit Feedback
          </Button>
          <Button
            variant="secondary"
            fullWidth={false}
            disabled={isApproved || approveProgressMutation.isPending}
            loading={approveProgressMutation.isPending}
            onClick={handleApprove}
          >
            {isApproved ? "Mentor Approved" : "Approve Mentor"}
          </Button>
        </div>
      </div>
    </div>
  );
}
